import { createClient } from '@/lib/supabase/client';

const EDGE_FUNCTION_URL = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/send-email`;

// Admin email address that receives all operational notifications
const ADMIN_EMAIL = process.env.NEXT_PUBLIC_ADMIN_EMAIL || 'admin@maritzgolf.co.za';

async function callEmailFunction(payload: object): Promise<void> {
  const supabase = createClient();
  const { data: { session } } = await supabase.auth.getSession();

  try {
    await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
        ...(session?.access_token ? { 'x-user-token': session.access_token } : {}),
      },
      body: JSON.stringify(payload),
    });
  } catch {
    // Email sending is non-blocking — silently fail so it never breaks the main flow
  }
}

export const emailService = {
  /**
   * Trigger 1: Booking confirmation sent to visitor after successful booking.
   */
  async sendBookingConfirmation(params: {
    to: string;
    visitorName: string;
    date: string;
    time: string;
    players: number;
    holes: number;
    cart: string;
    paymentMethod: string;
    total: number;
    bookingRef?: string;
    greenFees?: string;
    cartFees?: string;
  }): Promise<void> {
    await callEmailFunction({
      type: 'booking_confirmation',
      to: params.to,
      data: {
        visitorName: params.visitorName,
        date: params.date,
        time: params.time,
        players: params.players,
        holes: params.holes,
        cart: params.cart,
        paymentMethod: params.paymentMethod,
        total: params.total,
        bookingRef: params.bookingRef || null,
        greenFees: params.greenFees || null,
        cartFees: params.cartFees || null,
      },
    });
  },

  /**
   * Trigger 2: Registration verification sent to visitor after successful sign-up.
   */
  async sendRegistrationVerification(params: {
    to: string;
    visitorName: string;
    email: string;
    mobile: string;
    category: string;
    handicap?: number | string;
  }): Promise<void> {
    await callEmailFunction({
      type: 'registration_verification',
      to: params.to,
      data: {
        visitorName: params.visitorName,
        email: params.email,
        mobile: params.mobile,
        category: params.category,
        handicap: params.handicap || null,
      },
    });
  },

  /**
   * Trigger 3: Cancellation receipt sent to visitor when a booking is cancelled.
   */
  async sendCancellationReceipt(params: {
    to: string;
    visitorName: string;
    bookingRef: string;
    date: string;
    time: string;
    players: number;
    holes: number;
    cart: string;
    amountPaid: string;
    refundStatus: string;
    refundNote?: string;
    cancelledDate: string;
  }): Promise<void> {
    const refundLabels: Record<string, string> = {
      pending: 'Refund Pending – will be processed within 3–5 business days',
      processed: 'Refund Processed – please check your original payment method',
      not_applicable: 'No Refund Applicable',
    };

    await callEmailFunction({
      type: 'cancellation_receipt',
      to: params.to,
      data: {
        visitorName: params.visitorName,
        bookingRef: params.bookingRef,
        date: params.date,
        time: params.time,
        players: params.players,
        holes: params.holes,
        cart: params.cart,
        amountPaid: params.amountPaid,
        refundStatus: refundLabels[params.refundStatus] || params.refundStatus,
        refundNote: params.refundNote || null,
        cancelledDate: params.cancelledDate,
      },
    });
  },

  /**
   * Admin: Notify admin of a new booking confirmation.
   */
  async notifyAdminBooking(params: {
    visitorName: string;
    visitorEmail: string;
    bookingRef: string;
    date: string;
    time: string;
    players: number;
    holes: number;
    cart: string;
    paymentMethod: string;
    total: string | number;
  }): Promise<void> {
    await callEmailFunction({
      type: 'admin_booking_notification',
      to: ADMIN_EMAIL,
      data: {
        visitorName: params.visitorName,
        visitorEmail: params.visitorEmail,
        bookingRef: params.bookingRef,
        date: params.date,
        time: params.time,
        players: params.players,
        holes: params.holes,
        cart: params.cart,
        paymentMethod: params.paymentMethod,
        total: params.total,
      },
    });
  },

  /**
   * Admin: Notify admin of a new visitor registration.
   */
  async notifyAdminRegistration(params: {
    visitorName: string;
    email: string;
    mobile: string;
    category: string;
    handicap?: number | string;
    registeredAt: string;
  }): Promise<void> {
    await callEmailFunction({
      type: 'admin_registration_notification',
      to: ADMIN_EMAIL,
      data: {
        visitorName: params.visitorName,
        email: params.email,
        mobile: params.mobile,
        category: params.category,
        handicap: params.handicap || null,
        registeredAt: params.registeredAt,
      },
    });
  },

  /**
   * Admin: Notify admin of a booking modification.
   */
  async notifyAdminModification(params: {
    visitorName: string;
    visitorEmail: string;
    bookingRef: string;
    originalDate: string;
    originalTime: string;
    newDate: string;
    newTime: string;
    players: number;
    holes: number;
    cart: string;
    total: number;
    modifiedDate: string;
  }): Promise<void> {
    await callEmailFunction({
      type: 'admin_booking_modification_notification',
      to: ADMIN_EMAIL,
      data: {
        visitorName: params.visitorName,
        visitorEmail: params.visitorEmail,
        bookingRef: params.bookingRef,
        originalDate: params.originalDate,
        originalTime: params.originalTime,
        newDate: params.newDate,
        newTime: params.newTime,
        players: params.players,
        holes: params.holes,
        cart: params.cart,
        total: params.total,
        modifiedDate: params.modifiedDate,
      },
    });
  },

  /**
   * Admin: Notify admin of a booking cancellation.
   */
  async notifyAdminCancellation(params: {
    visitorName: string;
    visitorEmail: string;
    bookingRef: string;
    date: string;
    time: string;
    players: number;
    amountPaid: string;
    refundStatus: string;
    cancelledDate: string;
  }): Promise<void> {
    const refundLabels: Record<string, string> = {
      pending: 'Refund Pending',
      processed: 'Refund Processed',
      not_applicable: 'No Refund',
    };

    await callEmailFunction({
      type: 'admin_cancellation_notification',
      to: ADMIN_EMAIL,
      data: {
        visitorName: params.visitorName,
        visitorEmail: params.visitorEmail,
        bookingRef: params.bookingRef,
        date: params.date,
        time: params.time,
        players: params.players,
        amountPaid: params.amountPaid,
        refundStatus: refundLabels[params.refundStatus] || params.refundStatus,
        cancelledDate: params.cancelledDate,
      },
    });
  },

  /**
   * Trigger 4: Loyalty reward notification when visitor hits a round milestone (every 10 rounds).
   */
  async sendLoyaltyReward(params: {
    to: string;
    visitorName: string;
    roundsPlayed: number;
    loyaltyPoints: number;
    rewardDescription: string;
  }): Promise<void> {
    await callEmailFunction({
      type: 'loyalty_reward',
      to: params.to,
      data: {
        visitorName: params.visitorName,
        roundsPlayed: params.roundsPlayed,
        loyaltyPoints: params.loyaltyPoints,
        rewardDescription: params.rewardDescription,
      },
    });
  },

  /**
   * Trigger 5: Membership conversion message when admin invites a qualifying visitor.
   */
  async sendMembershipInvite(params: {
    to: string;
    visitorName: string;
    roundsPlayed: number;
  }): Promise<void> {
    await callEmailFunction({
      type: 'membership_conversion',
      to: params.to,
      data: {
        visitorName: params.visitorName,
        roundsPlayed: params.roundsPlayed,
      },
    });
  },

  /**
   * Trigger 6: Booking modification confirmation sent to visitor after they change their booking.
   */
  async sendBookingModification(params: {
    to: string;
    visitorName: string;
    bookingRef: string;
    originalDate: string;
    originalTime: string;
    newDate: string;
    newTime: string;
    players: number;
    holes: number;
    cart: string;
    total: number;
    modifiedDate: string;
  }): Promise<void> {
    await callEmailFunction({
      type: 'booking_modification',
      to: params.to,
      data: {
        visitorName: params.visitorName,
        bookingRef: params.bookingRef,
        originalDate: params.originalDate,
        originalTime: params.originalTime,
        newDate: params.newDate,
        newTime: params.newTime,
        players: params.players,
        holes: params.holes,
        cart: params.cart,
        total: params.total,
        modifiedDate: params.modifiedDate,
      },
    });
  },

  /**
   * Trigger 7: PayFast payment confirmation sent after a successful ITN callback.
   */
  async sendPayFastConfirmation(params: {
    to: string;
    visitorName: string;
    bookingRef: string;
    date: string;
    time: string;
    players: number;
    holes: number;
    cart: string;
    greenFees: string;
    cartFees: string;
    amountPaid: string;
    pfPaymentId: string;
  }): Promise<void> {
    await callEmailFunction({
      type: 'payfast_payment_confirmation',
      to: params.to,
      data: {
        visitorName: params.visitorName,
        bookingRef: params.bookingRef,
        date: params.date,
        time: params.time,
        players: params.players,
        holes: params.holes,
        cart: params.cart,
        greenFees: params.greenFees,
        cartFees: params.cartFees,
        amountPaid: params.amountPaid,
        pfPaymentId: params.pfPaymentId,
      },
    });
  },

  /**
   * Trigger 8: 24-hour tee time reminder sent automatically before each booking.
   */
  async sendTeeTimeReminder(params: {
    to: string;
    visitorName: string;
    bookingRef: string;
    date: string;
    time: string;
    players: number;
    holes: number;
    cart: string;
  }): Promise<void> {
    await callEmailFunction({
      type: 'tee_time_reminder',
      to: params.to,
      data: {
        visitorName: params.visitorName,
        bookingRef: params.bookingRef,
        date: params.date,
        time: params.time,
        players: params.players,
        holes: params.holes,
        cart: params.cart,
      },
    });
  },
};
